import React from 'react'

import { observer } from 'mobx-react-lite'
import { List, ListImperativeAPI } from 'react-window'

import { useMst } from '#state'
import { IReadonlyFolderListState } from '#state/types'
import { RowComponent } from '#types/react-window'

import {
  getLiveFolderList,
  getReadonlyFolderList,
  ListItem,
} from './folderListHelpers'
import { LiveFolderListing, ReadonlyFolderListing } from './FolderListing'

const LiveListingRow: RowComponent<{ data: ListItem[] }> = (props) => {
  return (
    <div style={props.style}>
      <LiveFolderListing item={props.data[props.index]} />
    </div>
  )
}

const ReadonlyListingRow: RowComponent<{ data: ListItem[] }> = (props) => {
  return (
    <div style={props.style}>
      <ReadonlyFolderListing item={props.data[props.index]} />
    </div>
  )
}

const FolderList = ({
  setList,
  listItems,
  Renderer,
}: {
  listItems: ListItem[]
  Renderer: typeof ReadonlyListingRow | typeof LiveListingRow
  setList?: (elt: ListImperativeAPI | null) => void
}) => {
  return (
    <List
      listRef={setList}
      rowCount={listItems.length}
      rowHeight={30}
      rowComponent={Renderer}
      rowProps={{ data: listItems }}
      style={{ paddingTop: 5, paddingBottom: 5 }}
    />
  )
}

export const ExplorerFolderList = observer(function ExplorerFolderList() {
  const mst = useMst()
  const listItems = getLiveFolderList(mst)
  // for the list, we use state instead of ref because we need to trigger
  // an effect when the ref gets set
  const [list, setList] = React.useState<ListImperativeAPI | null>(null)
  const [hasAutoscrolled, setHasAutoscrolled] = React.useState(false)

  React.useEffect(() => {
    if (!hasAutoscrolled && list) {
      const targetIndex = listItems.findIndex(
        (d) => d.folderId === mst.view.selectedFolderId,
      )
      if (targetIndex > -1) {
        // for some reason the scrolling fails without a little delay,
        // this is a teensy bit janky but ¯\_(ツ)_/¯
        setTimeout(() => {
          setHasAutoscrolled(true)
          list.scrollToRow({ index: targetIndex, align: 'smart' })
          mst.view.explorerState.clearCrosshairsFolderRequest()
        }, 100)
      }
    }
  }, [
    hasAutoscrolled,
    list,
    listItems,
    mst.view.explorerState,
    mst.view.selectedFolderId,
  ])

  // reset the has scrolled if the user requests a folder
  React.useEffect(() => {
    if (mst.view.explorerState.crosshairsRequestFolderId && hasAutoscrolled) {
      setHasAutoscrolled(false)
    }
  }, [hasAutoscrolled, mst.view.explorerState.crosshairsRequestFolderId])

  return (
    <FolderList
      listItems={listItems}
      Renderer={LiveListingRow}
      setList={setList}
    />
  )
})

export const ReadonlyFolderList = observer(function ReadonlyFolderList({
  folderListState,
}: {
  folderListState: IReadonlyFolderListState
}) {
  const mst = useMst()
  const [hasAutoscrolled, setHasAutoscrolled] = React.useState(false)
  const [list, setList] = React.useState<ListImperativeAPI | null>(null)
  const listItems = getReadonlyFolderList(mst, folderListState)
  const { selectedFolderId } = folderListState

  React.useEffect(() => {
    if (!hasAutoscrolled && list) {
      const targetIndex = listItems.findIndex(
        (d) => d.folderId === selectedFolderId,
      )
      if (targetIndex > -1) {
        setHasAutoscrolled(true)
        list.scrollToRow({ index: targetIndex, align: 'smart' })
      }
    }
  }, [hasAutoscrolled, list, listItems, folderListState, selectedFolderId])

  return (
    <FolderList
      listItems={listItems}
      Renderer={ReadonlyListingRow}
      setList={setList}
    />
  )
})
